import React, { useState, useMemo, useEffect } from "react";
import StatusBadge from "./StatusBadge";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import Pagination from "./Pagination";

const OrderList = ({ ordering, getOrderStatus }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const navigate = useNavigate();

  // ✅ reset page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [ordering]);

  // 📌 total pages
  const totalPages = Math.ceil(ordering.length / itemsPerPage);

  // 📌 slice data
  const currentOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return ordering.slice(start, start + itemsPerPage);
  }, [ordering, currentPage]);

  // 📌 scroll top
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // ✅ EMPTY
  if (ordering.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <p className="text-4xl mb-3">📭</p>
        <p className="text-lg font-medium">No orders</p>
        <p className="text-sm mt-2">
          Orders will appear here once customers place them
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* TABLE */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-gray-50">
            <tr className="text-gray-600">
              <th className="p-3 text-left">Order</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">Note</th>
              <th className="p-3 text-left">Payment</th>
              <th className="p-3 text-left">Order Status</th>
              <th className="p-3 text-left">Created</th>
            </tr>
          </thead>

          <tbody>
            {currentOrders.map((order) => {
              const status = getOrderStatus(order);
              const paymentStatus = order.payments?.paymentStatus;

              return (
                <tr
                  key={order._id}
                  onClick={() => navigate(`/order/preview/${order._id}`)}
                  className="border-b hover:bg-gray-50 cursor-pointer transition"
                >
                  {/* Order ID */}
                  <td className="p-3 font-semibold text-gray-800">
                    #{order._id.slice(-5)}
                  </td>

                  {/* Customer */}
                  <td className="p-3">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className="font-medium">
                        {order.customer_info?.firstname || "Guest"}
                      </span>
                      <span className="text-sm text-gray-400">
                        {order.customer_info?.phoneNumber}
                      </span>
                    </div>
                  </td>

                  <td className="p-3 max-w-[200px] align-top">
                    {order.customer_info?.message ? (
                      <p
                        className="flex items-start gap-2 text-md  rounded whitespace-normal break-words"
                        title={order.customer_info.message}
                      >
                        <MessageSquare
                          size={20}
                          className="text-neonYellow animate-neonFlickker mt-1"
                        />
                        <span>{order.customer_info.message}</span>
                      </p>
                    ) : (
                      <span className="text-xs text-gray-300 italic"></span>
                    )}
                  </td>

                  {/* Payment */}
                  <td className="p-3">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className="capitalize text-gray-700">
                        {order.payments?.paymentMethod}
                      </span>
                      <StatusBadge status={paymentStatus} />
                    </div>
                  </td>

                  {/* Order Status */}
                  <td className="p-3">
                    <StatusBadge status={status} />
                  </td>

                  {/* Created */}
                  <td className="p-3 text-gray-500">
                    {new Date(order.createdAt).toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default OrderList;
